import { useCallback, useMemo, useRef, useSyncExternalStore } from "react";
import type { ComputerRecord, ConnectionSnapshot, ConnState } from "@foreman/app-core";
import type { InboxEvent, SessionInfo } from "@foreman/protocol";
import { useApp, useHubSummaries } from "./ui";

export interface FeedEntry {
  computer: ComputerRecord;
  state: ConnState;
  snap: ConnectionSnapshot;
}

export interface FeedRow {
  computer: ComputerRecord;
  session: SessionInfo;
  /** The approval or question the agent is waiting on, if any. */
  ask: InboxEvent | undefined;
}

export const isAsk = (e: InboxEvent) => e.state === "open" && (e.kind === "approval" || e.kind === "question");

/** Every paired computer with its live connection snapshot. Re-renders when any of them changes; the same array until then. */
export function useFeed(): FeedEntry[] {
  const { hub } = useApp();
  const summaries = useHubSummaries(hub);
  const cache = useRef<{ snaps: (ConnectionSnapshot | undefined)[]; summaries: typeof summaries; out: FeedEntry[] } | null>(null);

  const subscribe = useCallback(
    (cb: () => void) => {
      const offs = [hub.subscribe(cb), ...summaries.map((s) => hub.get(s.computer.id)?.subscribe(cb))];
      return () => offs.forEach((off) => off?.());
    },
    [hub, summaries],
  );
  const getSnapshot = useCallback(() => {
    const snaps = summaries.map((s) => hub.get(s.computer.id)?.getSnapshot());
    const prev = cache.current;
    if (prev && prev.summaries === summaries && prev.snaps.length === snaps.length && prev.snaps.every((p, i) => p === snaps[i])) return prev.out;
    const out = summaries.flatMap((s, i) => (snaps[i] ? [{ computer: s.computer, state: s.state, snap: snaps[i]! }] : []));
    cache.current = { snaps, summaries, out };
    return out;
  }, [hub, summaries]);
  return useSyncExternalStore(subscribe, getSnapshot);
}

/** Every session on every computer: what needs you first, then what is running, then what ended (newest first within each). */
export function useSessionRows(feed: FeedEntry[]): { needs: FeedRow[]; running: FeedRow[]; ended: FeedRow[] } {
  return useMemo(() => {
    const rows: FeedRow[] = feed.flatMap(({ computer, snap }) =>
      snap.sessions.map((session) => ({ computer, session, ask: snap.inbox.find((e) => e.sessionId === session.id && isAsk(e)) })),
    );
    const byNewest = (a: FeedRow, b: FeedRow) => b.session.startedAt - a.session.startedAt;
    return {
      needs: rows.filter((r) => r.session.status === "running" && r.ask).sort(byNewest),
      running: rows.filter((r) => r.session.status === "running" && !r.ask).sort(byNewest),
      ended: rows.filter((r) => r.session.status !== "running").sort(byNewest),
    };
  }, [feed]);
}
